import { useState, useEffect } from "react";
import DashboardWrapper from "../components/layout/DashboardWrapper";
import { httpsCallable } from "firebase/functions";
import { functions, db } from "../firebase/config";
import { useIndexedUserData } from "../hooks/useIndexedUserData";
import { adminService } from "../services/adminService";
import { doc, getDoc } from "firebase/firestore";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";

// GA4 Property ID - replace with your actual property ID
const GA4_PROPERTY_ID = "465798180";

interface DailyActiveUsersData {
    date: string;
    activeUsers: number;
}

interface DailyActiveUsersResponse {
    success: boolean;
    propertyId: string;
    startDate: string;
    endDate: string;
    data: DailyActiveUsersData[];
    totalActiveUsers: number;
}

interface MonthlyUserData {
    month: string;
    newUsers: number;
    totalUsers: number;
}

interface AuditResult {
    success: boolean;
    totalAuthUsers: number;
    totalFirestoreUsers: number;
    missingCount: number;
    missingEmails: string[];
    checkedAt: string;
}

interface OrphanedUser {
    uid: string;
    email?: string;
}

interface OrphanedAuditResult {
    success: boolean;
    totalFirestoreUsers: number;
    orphanedFirestoreUsers: OrphanedUser[];
    orphanedCount: number;
    checkedAt: string;
}

const AnalyticsPage: React.FC = () => {
    const [dailyActiveUsers, setDailyActiveUsers] = useState<number | null>(null);
    const [loadingDAU, setLoadingDAU] = useState(true);
    const [monthlyData, setMonthlyData] = useState<MonthlyUserData[]>([]);
    const [loadingMonthly, setLoadingMonthly] = useState(true);
    const [showDiscrepancyModal, setShowDiscrepancyModal] = useState(false);
    // const [auditLoading, setAuditLoading] = useState(false);
    // const [auditResult, setAuditResult] = useState<AuditResult | null>(null);
    // const [auditError, setAuditError] = useState<string | null>(null);
    // const [orphanedAuditLoading, setOrphanedAuditLoading] = useState(false);
    // const [orphanedAuditResult, setOrphanedAuditResult] = useState<OrphanedAuditResult | null>(null);
    // const [orphanedAuditError, setOrphanedAuditError] = useState<string | null>(null);
    const [backfillLoading, setBackfillLoading] = useState(false);
    const [backfillSuccess, setBackfillSuccess] = useState<number | null>(null);
    const [backfillError, setBackfillError] = useState<string | null>(null);
    const { stats } = useIndexedUserData();
    const [userCount, setUserCount] = useState<number | null>(null);
    const [coachCount, setCoachCount] = useState<number | null>(null);

    useEffect(() => {
        document.title = 'Dashboard - Peak^ Admin';

        const fetchUserCount = async () => {
            try {
                const count = await adminService.getUserCount();
                setUserCount(count);
            } catch (error) {
                console.error('Error fetching user count:', error);
            }
        };

        const fetchCoachCount = async () => {
            try {
                const count = await adminService.getCoachCount();
                setCoachCount(count);
            } catch (error) {
                console.error('Error fetching coach count:', error);
            }
        };

        fetchUserCount();
        fetchCoachCount();

    }, [userCount, coachCount]);

    const fetchMonthlyUsers = async () => {
        try {
            const docRef = doc(db, "adminStats", "newUsersByMonth");
            const docSnap = await getDoc(docRef);

            if (docSnap.exists()) {
                const data = docSnap.data();
                // Build sorted array from document fields (keys like "01-2025")
                const months: MonthlyUserData[] = Object.entries(data)
                    .filter(([key]) => /^\d{2}-\d{4}$/.test(key))
                    .sort(([a], [b]) => {
                        const [mA, yA] = a.split("-");
                        const [mB, yB] = b.split("-");
                        return `${yA}-${mA}`.localeCompare(`${yB}-${mB}`);
                    })
                    .map(([month, value]) => {
                        const v = value as { newUsers?: number; cumulativeUsers?: number; totalUsers?: number };
                        return {
                            month,
                            newUsers: v.newUsers ?? 0,
                            totalUsers: v.cumulativeUsers ?? v.totalUsers ?? 0,
                        };
                    });

                setMonthlyData(months);
            }
        } catch (error) {
            console.error("Error fetching monthly user data:", error);
        } finally {
            setLoadingMonthly(false);
        }
    };

    useEffect(() => {
        fetchMonthlyUsers();
    }, []);

    const last12Months = monthlyData.slice(-12);

    const formatMonth = (month: string) => {
        const [m, year] = month.split("-");
        const date = new Date(parseInt(year), parseInt(m) - 1);
        return date.toLocaleDateString("en-US", { month: "short", year: "2-digit" });
    };

    const chartData = last12Months.map((d) => ({
        ...d,
        label: formatMonth(d.month),
    }));

    const calculateRemainingUsers = () => {
        if (userCount === null) return null;
        const premium = stats.premiumUsers ?? 0;
        const coach = stats.coachUsers ?? 0;
        const remaining = userCount - premium - coach;
        return remaining >= 0 ? remaining : 0;
    };

    const handleBackfill = async () => {
        setBackfillLoading(true);
        setBackfillSuccess(null);
        setBackfillError(null);
        try {
            const backfillFn = httpsCallable<unknown, { success: boolean; monthsBackfilled: number }>(functions, "backfillNewUsersByMonth");
            const res = await backfillFn({});
            setBackfillSuccess(res.data.monthsBackfilled);
            setLoadingMonthly(true);
            await fetchMonthlyUsers();
        } catch (err: unknown) {
            setBackfillError(err instanceof Error ? err.message : "Backfill failed");
        } finally {
            setBackfillLoading(false);
        }
    };

    const handleExportCSV = () => {
        const rows = [
            ["Month", "New Users", "Total Users"],
            ...monthlyData.map((row) => [formatMonth(row.month), row.newUsers, row.totalUsers]),
        ];
        const csv = rows.map((r) => r.join(",")).join("\n");
        const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `peak-monthly-user-growth-${new Date().toISOString().slice(0, 10)}.csv`;
        a.click();
        URL.revokeObjectURL(url);
    };

    // const handleRunOrphanedAudit = async () => {
    //     setOrphanedAuditLoading(true);
    //     setOrphanedAuditResult(null);
    //     setOrphanedAuditError(null);
    //     try {
    //         const auditFn = httpsCallable<unknown, OrphanedAuditResult>(functions, "auditFirestoreVsAuth");
    //         const res = await auditFn({});
    //         setOrphanedAuditResult(res.data);
    //     } catch (err: unknown) {
    //         setOrphanedAuditError(err instanceof Error ? err.message : "Audit failed");
    //     } finally {
    //         setOrphanedAuditLoading(false);
    //     }
    // };

    // const handleRunAudit = async () => {
    //     setAuditLoading(true);
    //     setAuditResult(null);
    //     setAuditError(null);
    //     try {
    //         const auditFn = httpsCallable<unknown, AuditResult>(functions, "auditAuthVsFirestore");
    //         const res = await auditFn({});
    //         setAuditResult(res.data);
    //     } catch (err: unknown) {
    //         setAuditError(err instanceof Error ? err.message : "Audit failed");
    //     } finally {
    //         setAuditLoading(false);
    //     }
    // };

    return (
        <DashboardWrapper>
            <div className="w-full flex flex-col gap-4 md:gap-6 md:h-[calc(100vh-64px)] p-4 md:p-6 overflow-hidden">
                <h1 className="text-2xl font-semibold mb-4">Peak^ Metrics</h1>

                <div className="w-full grid grid-cols-4 gap-2 md:gap-4 shrink-0">
                    <div className="w-full bg-white rounded-xl flex flex-col p-2 md:p-4 gap-2 md:gap-4 items-start justify-between border">
                        <div className="text-xl md:text-3xl font-bold">{userCount?.toLocaleString() ?? '—'}</div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Total users</div>
                    </div>
                    <div className="w-full bg-white rounded-xl flex flex-col p-2 md:p-4 gap-2 md:gap-4 items-start justify-between border">
                        <div className="text-xl md:text-3xl font-bold text-blue-700">{ stats.premiumUsers?.toLocaleString() ?? '—' } <span className="text-lg opacity-80">{ ((stats.premiumUsers ?? 0) / (userCount ?? 1) * 100).toFixed(1) }%</span></div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Paying Premium users</div>
                    </div>
                    <div className="w-full bg-white rounded-xl flex flex-col p-2 md:p-4 gap-2 md:gap-4 items-start justify-between border">
                        <div className="text-xl md:text-3xl font-bold text-green-700">{ stats.coachUsers?.toLocaleString() ?? '—' } <span className="text-lg opacity-80">{ ((stats.coachUsers ?? 0) / (userCount ?? 1) * 100).toFixed(1) }%</span></div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Paying Coach users</div>
                    </div>
                    <div className="w-full bg-white rounded-xl flex flex-col p-2 md:p-4 gap-2 md:gap-4 items-start justify-between border">
                        <div className="text-xl md:text-3xl font-bold text-gray-500">{ calculateRemainingUsers()?.toLocaleString() ?? '—' }</div>
                        <div className="text-[10px] md:text-xs text-gray-500 uppercase">Default users</div>
                    </div>
                </div>

                {/* New Users by Month - Chart & Table */}
                <div className="w-full flex-1 min-h-0 grid grid-cols-8 gap-4">

                    <div className="w-full col-span-5 bg-white rounded-xl border p-4 md:p-6">
                        <h2 className="text-sm font-medium text-gray-500 uppercase mb-4">New Users by Month (Last 12 Months)</h2>
                        {loadingMonthly ? (
                            <div className="h-64 flex items-center justify-center">
                                <span className="loading loading-spinner loading-md"></span>
                            </div>
                        ) : chartData.length > 0 ? (
                            <div className="h-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 20 }}>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f0f0f0" />
                                        <XAxis dataKey="label" tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                                        <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
                                        <Tooltip
                                            contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", boxShadow: "0 1px 3px rgba(0,0,0,0.1)" }}
                                            formatter={(value) => [Number(value).toLocaleString(), "New Users"]}
                                        />
                                        <Bar dataKey="newUsers" fill="rgb(156 163 175)" radius={[4, 4, 0, 0]} />
                                    </BarChart>
                                </ResponsiveContainer>
                            </div>
                        ) : (
                            <div className="h-64 flex items-center justify-center text-gray-400 text-sm">No data available</div>
                        )}
                    </div>

                    {/* New Users by Month - Table */}
                    <div className="w-full col-span-3 bg-white rounded-xl border overflow-hidden flex flex-col">
                        <div className="px-4 md:px-6 py-4 border-b bg-gray-50 flex items-center justify-between shrink-0">
                            <h2 className="text-sm font-medium text-gray-500 uppercase">Monthly User Growth</h2>
                            <div className="flex items-center gap-3">
                                {/* <button
                                    onClick={handleBackfill}
                                    disabled={backfillLoading}
                                    className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30 flex items-center gap-1"
                                    title="Recompute monthly data from Firebase Auth history"
                                >
                                    {backfillLoading ? (
                                        <span className="loading loading-spinner loading-xs"></span>
                                    ) : (
                                        <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                            <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-3.87"/>
                                        </svg>
                                    )}
                                    {backfillLoading ? "Backfilling…" : backfillSuccess !== null ? `Backfilled ${backfillSuccess} months` : "Backfill"}
                                </button>
                                {backfillError && (
                                    <span className="text-xs text-red-500">{backfillError}</span>
                                )} */}
                                <button
                                    onClick={handleExportCSV}
                                    disabled={monthlyData.length === 0}
                                    className="text-xs text-gray-500 hover:text-gray-800 disabled:opacity-30 flex items-center gap-1"
                                >
                                    <svg xmlns="http://www.w3.org/2000/svg" className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
                                    </svg>
                                    Export CSV
                                </button>
                                <button
                                    onClick={() => setShowDiscrepancyModal(true)}
                                    className="text-xs text-blue-600 hover:text-blue-800 underline"
                                >
                                    Discrepancy?
                                </button>
                            </div>
                        </div>
                        {loadingMonthly ? (
                            <div className="p-8 flex items-center justify-center">
                                <span className="loading loading-spinner loading-md"></span>
                            </div>
                        ) : monthlyData.length > 0 ? (
                            <div className="flex-1 min-h-0 overflow-y-auto">
                                <table className="w-full text-sm">
                                    <thead className="sticky top-0 bg-white">
                                        <tr className="border-b bg-gray-50/50">
                                            <th className="text-left px-4 md:px-6 py-3 text-xs font-medium text-gray-500 uppercase">Month</th>
                                            <th className="text-right px-4 md:px-6 py-3 text-xs font-medium text-gray-500 uppercase">New Users</th>
                                            <th className="text-right px-4 md:px-6 py-3 text-xs font-medium text-gray-500 uppercase">Total Users</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {[...monthlyData].reverse().map((row) => (
                                            <tr key={row.month} className="border-b last:border-b-0 hover:bg-gray-50 transition-colors">
                                                <td className="px-4 md:px-6 py-3 font-medium">{formatMonth(row.month)}</td>
                                                <td className="px-4 md:px-6 py-3 text-right">{row.newUsers.toLocaleString()}</td>
                                                <td className="px-4 md:px-6 py-3 text-right">{row.totalUsers.toLocaleString()}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        ) : (
                            <div className="p-8 text-center text-gray-400 text-sm">No data available</div>
                        )}
                    </div>
                </div>
            </div>

            {showDiscrepancyModal && (
                <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setShowDiscrepancyModal(false)}>
                    <div className="bg-white rounded-xl shadow-xl max-w-lg w-full" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center justify-between px-6 py-4 border-b">
                            <h3 className="text-lg font-semibold text-gray-900">Data Discrepancy</h3>
                            <button onClick={() => setShowDiscrepancyModal(false)}>
                                <span className="text-gray-400 hover:text-gray-700 text-xl">&times;</span>
                            </button>
                        </div>
                        <div className="px-6 py-4">
                            <p className="text-sm text-gray-700 leading-relaxed">
                                The cumulative total shown in this table may be <strong>lower</strong> than the total user count displayed at the top of the page. The user count at the top reflects <strong>live Firestore data</strong>, while the monthly growth figures are recomputed on a <strong>weekly schedule</strong> and may not yet include the most recently registered users.
                            </p>
                            <p className="text-sm text-gray-700 leading-relaxed mt-3">
                                This gap will close automatically once the next scheduled update runs.
                            </p>

                            {/* <div className="mt-4 pt-4 border-t">
                                <button
                                    onClick={handleRunAudit}
                                    disabled={auditLoading}
                                    className="btn btn-sm btn-neutral w-full"
                                >
                                    {auditLoading ? (
                                        <><span className="loading loading-spinner loading-xs mr-2"></span>Running audit (may take ~2 min)…</>
                                    ) : "Run Auth vs Firestore Audit"}
                                </button>

                                {auditError && (
                                    <p className="mt-3 text-sm text-red-600">{auditError}</p>
                                )}

                                {auditResult && (
                                    <div className="mt-4 space-y-2">
                                        <div className="grid grid-cols-3 gap-2 text-center">
                                            <div className="bg-gray-50 rounded-lg p-2">
                                                <div className="text-lg font-bold">{auditResult.totalAuthUsers.toLocaleString()}</div>
                                                <div className="text-[10px] text-gray-500 uppercase">Auth users</div>
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-2">
                                                <div className="text-lg font-bold">{auditResult.totalFirestoreUsers.toLocaleString()}</div>
                                                <div className="text-[10px] text-gray-500 uppercase">Firestore users</div>
                                            </div>
                                            <div className="bg-red-50 rounded-lg p-2">
                                                <div className="text-lg font-bold text-red-600">{auditResult.missingCount.toLocaleString()}</div>
                                                <div className="text-[10px] text-gray-500 uppercase">Missing</div>
                                            </div>
                                        </div>

                                        {auditResult.missingEmails.length > 0 && (
                                            <div>
                                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                                                    Emails in Auth but missing from Firestore
                                                </p>
                                                <div className="max-h-48 overflow-y-auto rounded-lg border bg-gray-50 p-2 space-y-1">
                                                    {auditResult.missingEmails.map((email) => (
                                                        <div key={email} className="text-xs font-mono text-gray-700 px-1">{email}</div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <p className="text-[10px] text-gray-400 text-right">
                                            Checked {new Date(auditResult.checkedAt).toLocaleString()}
                                        </p>
                                    </div>
                                )}
                            </div>

                            <div className="mt-4 pt-4 border-t">
                                <button
                                    onClick={handleRunOrphanedAudit}
                                    disabled={orphanedAuditLoading}
                                    className="btn btn-sm btn-neutral w-full"
                                >
                                    {orphanedAuditLoading ? (
                                        <><span className="loading loading-spinner loading-xs mr-2"></span>Running audit (may take ~2 min)…</>
                                    ) : "Run Firestore vs Auth Audit"}
                                </button>

                                {orphanedAuditError && (
                                    <p className="mt-3 text-sm text-red-600">{orphanedAuditError}</p>
                                )}

                                {orphanedAuditResult && (
                                    <div className="mt-4 space-y-2">
                                        <div className="grid grid-cols-3 gap-2 text-center">
                                            <div className="bg-gray-50 rounded-lg p-2">
                                                <div className="text-lg font-bold">{orphanedAuditResult.totalFirestoreUsers.toLocaleString()}</div>
                                                <div className="text-[10px] text-gray-500 uppercase">Firestore users</div>
                                            </div>
                                            <div className="bg-gray-50 rounded-lg p-2 col-span-1">
                                                <div className="text-lg font-bold">{(orphanedAuditResult.totalFirestoreUsers - orphanedAuditResult.orphanedCount).toLocaleString()}</div>
                                                <div className="text-[10px] text-gray-500 uppercase">With Auth</div>
                                            </div>
                                            <div className="bg-red-50 rounded-lg p-2">
                                                <div className="text-lg font-bold text-red-600">{orphanedAuditResult.orphanedCount.toLocaleString()}</div>
                                                <div className="text-[10px] text-gray-500 uppercase">Orphaned</div>
                                            </div>
                                        </div>

                                        {orphanedAuditResult.orphanedFirestoreUsers.length > 0 && (
                                            <div>
                                                <p className="text-xs font-medium text-gray-500 uppercase mb-1">
                                                    Firestore docs with no Auth account
                                                </p>
                                                <div className="max-h-48 overflow-y-auto rounded-lg border bg-gray-50 p-2 space-y-1">
                                                    {orphanedAuditResult.orphanedFirestoreUsers.map((u) => (
                                                        <div key={u.uid} className="text-xs font-mono text-gray-700 px-1">
                                                            {u.email ?? <span className="text-gray-400">{u.uid}</span>}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        <p className="text-[10px] text-gray-400 text-right">
                                            Checked {new Date(orphanedAuditResult.checkedAt).toLocaleString()}
                                        </p>
                                    </div>
                                )}
                            </div> */}
                        </div>
                        <div className="flex justify-end px-6 py-4 border-t">
                            <button onClick={() => setShowDiscrepancyModal(false)} className="btn btn-sm btn-ghost">
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </DashboardWrapper>
    );
};

export default AnalyticsPage;
